import { ACFFormElement, FormValuesType } from "./Form";
import { genFieldName } from "./renderFields";
import { ImageType } from "@/wp/types/acf-header-types";

export type FormElementBase = {
  type:
    | Exclude<ACFFormElement["input_type"], "fromgroup-start" | "formgroup-end">
    | "fieldset"
    | "captcha"
    | "column";
  inputWidth: ACFFormElement["input_width"];
  renderCondition?: (values: FormValuesType) => boolean; //whether the element is rendered or not
  getValue?: (
    values: FormValuesType,
    options?: { cleverReach?: boolean }, //if cleverReach is true, special values for cleverReach are used for example cleverReachName is used for location dropdowns
  ) => string; //returns value to send in email
  label: string;
  emailLabel?: string;
  name: string;
  placeholder?: string;
  ariaLabel?: string;
  isRequired?: boolean;
  requiredEmptyError?: string;
  typeErrorMessage?: string;
  wrapperClassName?: string;
  display_condition: boolean | false;
  condition_field: string | "";
  condition_value: string | "";
  children?: FormElement[];
};

export type DefaulttFormElement = FormElementBase & {
  type: "text" | "email" | "tel" | "captcha" | "url";
};

export type TextareaFormElement = FormElementBase & {
  type: "textarea";
  rows: number;
};

export type NumberFormElement = FormElementBase & {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
  children?: FormElement[];
};

export type FieldsetFormElement = FormElementBase & {
  type: "fieldset";
  children: FormElement[];
  style?: "h3" | "label";
};

export type ColumnElement = FormElementBase & {
  type: "column";
  children: FormElement[];
};

export type InfoTextFormElement = FormElementBase & {
  type: "infotext";
  infoText: string;
  infoTextOptions: ACFFormElement["infotext"];
};

export type DropdownFormElement = FormElementBase & {
  type: "dropwdown";
  placeholder?: string;
  multiSelect: boolean;
  getMailTo?: (values: FormValuesType) => string | undefined;
  items: { label: string; value: string }[];
};

export type RadioFormElement = FormElementBase & {
  type: "radiobutton";
  options: { label: string; value: string }[];
};

export type CheckboxFormElement = FormElementBase & {
  type: "checkbox";
  labelCheckbox: string;
};

export type DatepickerFormElement = FormElementBase & {
  type: "datepicker";
  min?: string;
  max?: string;
};

export type BoxFormElement = FormElementBase & {
  type: "box";
  box_headline: string;
  box_text: string;
  box_image: ImageType | false;
};

export type FileFormElement = FormElementBase & {
  type: "file";
  labelUploadButton: string;
  ariaLabelUploadButton: string;
  imageUploadButton: ImageType | false;
  labelRemoveButton: string;
  ariaLabelRemoveButton: string;
  imageRemoveButton: ImageType | false;
};

export type FormElement =
  | DefaulttFormElement
  | NumberFormElement
  | InfoTextFormElement
  | DropdownFormElement
  | RadioFormElement
  | DatepickerFormElement
  | FieldsetFormElement
  | CheckboxFormElement
  | ColumnElement
  | TextareaFormElement
  | FileFormElement
  | BoxFormElement;

interface CreateFormStructureProps {
  formId: string;
  futureDayData?:
    | {
        dates: { label: string; emailValue: string }[];
        locations: number[];
      }
    | undefined;
  fields: ACFFormElement[];
  friendlyCaptchaApplicationKey: string | undefined;
  getLabel: ((key: string) => string) | undefined;
}

export const createFormStructure = ({
  formId,
  futureDayData,
  fields,
  friendlyCaptchaApplicationKey,
  getLabel,
}: CreateFormStructureProps): FormElement[] => {
  let formElements: FormElement[] = [];

  let insideGroup = false;
  let insideColumn = false;
  let groupWidth: ACFFormElement["input_width"] | null = null;
  let groupLabel: ACFFormElement["fieldlabel"] | null = null;
  let groupElements: ACFFormElement[] = [];

  let groupCondition = {
    display_condition: false,
    condition_field: "",
    condition_value: "",
  };

  const columnElements: FormElement[] = [];

  const createFormGroup = () => {
    formElements.push({
      type: "fieldset",
      inputWidth: groupWidth || "full",
      label: groupLabel || "",
      name: "",
      children: createFormStructure({
        fields: groupElements,
        friendlyCaptchaApplicationKey,
        getLabel,
        formId,
        futureDayData,
      }),
      display_condition: groupCondition.display_condition,
      condition_field: groupCondition.condition_field,
      condition_value: groupCondition.condition_value,
    });
  };

  const createColumn = () => {
    columnElements.push({
      type: "column",
      children: formElements,
      name: "",
      inputWidth: "full",
      label: "",
      display_condition: false,
      condition_field: "",
      condition_value: "",
    });
    formElements = [];
  };

  for (const field of fields) {
    const newformElements: FormElement[] = [];

    if (field.new_formgroup_start) {
      if (!insideGroup) {
        // start of first group
        insideGroup = true;
      } else {
        createFormGroup();
        groupElements = [];
      }
      groupWidth = "full";
      groupLabel = field.legend;

      groupCondition = {
        display_condition: field.new_formgroup_start && field.display_condition,
        condition_field: field.condition_field,
        condition_value: field.condition_value,
      };
    }

    if (field.new_column_start) {
      insideColumn = true;
      createColumn();
    }

    if (insideGroup) {
      groupElements.push({
        ...field,
        new_formgroup_start: false, //prevents that a new fieldgroup is created when running createFormGroup (already done in the lines above)
        new_column_start: false, //prevents that a new column is created when running createFormGroup (already done in the lines above)
      });
      continue;
    }
    const name = field.feld_name || genFieldName(field.fieldlabel);
    const commonProps = {
      name,
      label: field.fieldlabel,
      inputWidth: field.input_width,
      emailLabel: field.form_label,
      ariaLabel: field.aria_label,
      isRequired: field.is_required,
      placeholder: field.placeholder,
      getValue: (values: FormValuesType) => values[name]?.toString() || "",
      requiredEmptyError: field.required_empty_error,
      typeErrorMessage: field.type_error_message,
      display_condition: field.new_formgroup_start && field.display_condition,
      condition_field: field.condition_field,
      condition_value: field.condition_value,
    };

    switch (field.input_type) {
      case "infotext":
        formElements.push({
          ...commonProps,
          type: field.input_type,
          infoText: field.infotext,
          infoTextOptions: field.infotext,
          getValue: () => field.infotext,
        });
        break;
      case "text":
      case "email":
      case "url":
      case "tel":
        formElements.push({
          ...commonProps,
          type: field.input_type,
        });
        break;
      case "textarea":
        formElements.push({
          ...commonProps,
          type: field.input_type,
          rows: field.anzahl_zeilen_textarea,
        });
        break;
      case "number":
        formElements.push({
          ...commonProps,
          type: field.input_type,
          min:
            typeof field.number_min === "number" ? field.number_min : undefined,
          max:
            typeof field.number_max === "number" ? field.number_max : undefined,
          step:
            typeof field.number_step === "number"
              ? field.number_step
              : undefined,
        });
        break;
      case "dropwdown":
        const otherItems: {
          label: string;
          emailLabel: string;
        }[] = (field.dropdown_other_options || []).map((o) => ({
          label: o.fieldlabel,
          emailLabel: o.value,
        }));

        const items: {
          label: string;
          value: string;
          emailLabel?: string;
          cleverReachName?: string;
        }[] = otherItems.map((e, key) => {
          return { ...e, value: key?.toString() || "" };
        });

        formElements.push({
          ...commonProps,
          type: field.input_type,
          multiSelect: field.dropdown_multiselect,
          items: items,
          getValue: (values: FormValuesType, options) => {
            if (!field.dropdown_multiselect) {
              const index = Number(values[name]);
              const value = values?.[name]?.toString() || "";
              const element = isNaN(index) ? undefined : items[index];
              if (options?.cleverReach) {
                return element
                  ? element.cleverReachName ||
                      element.emailLabel ||
                      element.label
                  : "";
              }
              return element
                ? element.emailLabel || element.label
                : value
                  ? (value as string)
                  : "";
            } else {
              const selectedIdsStrings = values[name];
              if (typeof selectedIdsStrings !== "object") {
                return "";
              }
              const selectedIds = selectedIdsStrings.map((e) => Number(e));
              const selectedElements = items
                .filter((i, key) => selectedIds.includes(key))
                .map((i) => {
                  if (options?.cleverReach) {
                    return i.cleverReachName || i.emailLabel || i.label || "";
                  }
                  return i.emailLabel || i.label || "";
                });
              return selectedElements.join(", ");
            }
          },
        });
        break;
      case "checkbox":
        formElements.push({
          ...commonProps,
          type: field.input_type,
          labelCheckbox: field.label_inneres,
          getValue: (values: FormValuesType) => {
            return !!values[name] ? field.formvalue || "Ja" : "Nein";
          },
        });

        if (
          field.friendly_captcha_after_checked &&
          friendlyCaptchaApplicationKey
        ) {
          formElements.push({
            type: "captcha",
            renderCondition: (values) => {
              return !!values[name];
            },
            name: name + "-captcha",
            inputWidth: commonProps.inputWidth,
            label: "",
            display_condition:
              field.new_formgroup_start && field.display_condition,
            condition_field: field.condition_field,
            condition_value: field.condition_value,
            getValue: (values: FormValuesType) => {
              return !!values[name] ? "Ja" : "Nein";
            },
          });
        }

        break;
      case "radiobutton":
        const options: { label: string; value: string; emailLabel: string }[] =
          (
            field.radio_inputs !== false && field.radio_inputs
              ? field.radio_inputs
              : []
          ).map((r: { fieldlabel: string; value: string }, key: number) => {
            return {
              label: r.fieldlabel,
              value: key?.toString(),
              emailLabel: r.value,
            };
          });

        const radioButton: FormElement = {
          ...commonProps,
          type: "radiobutton",
          options,
          getValue: (values: FormValuesType) => {
            const index = Number(values[name]);
            const element = isNaN(index) ? undefined : options[index];
            return element ? element.emailLabel || element.label : "";
          },
        };

        //TODO: better define which fields need extra text field
        //if there is a option like "Andere" in radio and selected, render a text field after the radio buttons AND return the value of this
        //text field as value fo the radio
        const otherIndex = options.findIndex(
          (o) =>
            o.label.toLocaleLowerCase().includes("ander") ||
            o.label.toLocaleLowerCase().includes("other"),
        );
        if (otherIndex !== -1) {
          const otherFieldName = commonProps.name + "-other";
          const otherField: FormElement = {
            ...commonProps,
            placeholder: options[otherIndex].label,
            type: "text",
            name: otherFieldName,
            label: commonProps.label + " Andere",
            inputWidth: "full",
            wrapperClassName: "pt-0",
            renderCondition: (values: FormValuesType) => {
              const index = Number(values[name]);
              const element = isNaN(index) ? undefined : options[index];
              if (
                element?.label.toLocaleLowerCase().includes("ander") ||
                element?.label.toLocaleLowerCase().includes("other")
              ) {
                return true;
              }
              return false;
            },
          };

          //if radio has "other" options render radios and other text field inside of extra fieldset
          formElements.push({
            type: "fieldset",
            children: [
              {
                ...radioButton,
                inputWidth: "full",
                wrapperClassName: "pb-0",
                getValue: (values: FormValuesType) => {
                  const index = Number(values[name]);
                  if (index === otherIndex) {
                    //if "other" option is selected in radio return the value of the text field for the radio button
                    return values?.[otherFieldName]?.toString() || "";
                  }
                  return radioButton.getValue?.(values) ?? "";
                },
              },
              otherField,
            ],
            inputWidth: commonProps.inputWidth,
            label: "",
            name: commonProps.name + "-with-other",
            display_condition:
              field.new_formgroup_start && field.display_condition,
            condition_field: field.condition_field,
            condition_value: field.condition_value,
          });
        } else {
          formElements.push(radioButton);
        }
        break;
      case "datepicker":
        const min =
          field.date_type === "future-events"
            ? new Date(Date.now() + 86400000).toISOString().split("T")[0]
            : undefined;
        const max =
          field.date_type === "past-birthdays"
            ? new Date(Date.now() - 86400000).toISOString().split("T")[0]
            : undefined;
        formElements.push({
          ...commonProps,
          getValue: (formValues) => {
            const value = formValues[commonProps.name];
            if (!value) return "";
            if (typeof value !== "string") {
              console.error("Datepicker value is not a string");
              return "";
            }
            try {
              return new Date(value).toLocaleDateString("de", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
            } catch {
              console.error("Datepicker value is not a valid date");
              return "";
            }
          },
          type: "datepicker",
          min: min,
          max: max,
        });
        break;
      case "file":
        formElements.push({
          ...commonProps,
          type: "file",
          labelUploadButton: field.label_upload_button,
          ariaLabelUploadButton: field.aria_label_upload_button,
          imageUploadButton: field.image_upload_button,
          labelRemoveButton: field.label_remove_button,
          ariaLabelRemoveButton: field.aria_label_remove_button,
          imageRemoveButton: field.image_remove_button,
          getValue: (values: FormValuesType) => {
            const value = values[name];
            return typeof value === "string" ? value : "";
          },
        });
        break;
      case "box":
        formElements.push({
          ...commonProps,
          type: "box",
          box_headline: field.box_headline,
          box_text: field.box_text,
          box_image: field.box_image,
        });
        break;
    }
    formElements.push(...newformElements);
  }

  if (insideGroup) {
    // create last form group
    createFormGroup();
  }
  if (insideColumn) {
    createColumn();
    return columnElements;
  } else {
    return formElements;
  }
};
